import { ParcTenancyBienDto, ParcTenancyFileResponse } from "api/real-estate";
import GenericTable from "components/display/Table/Table";
import { Column } from "components/display/Table/types";
import { GenericMessage } from "components/feedback/Message";
import { Checkbox } from "components/forms/fragments/Checkbox";
import { Sizes } from "components/forms/NumberSelector";
import { EyeIcon } from "components/icons/Icons";
import { Button } from "components/ui/Button";
import { SubscriptionProcessContext, useSubscriptionProcess } from "features/subscription-process/contexts/SubscriptionProcessContext";
import { usePanel } from "hooks/usePanel";
import { Search } from "lucide-react";
import { FC, useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { realEstateAPI } from "services/swagger-api/realEstateAPI";
import { formatAddress, formatEuroPrice } from "../../utils/realEstateFormatters";
import { ParcBienDetailPanel } from "./ParcBienDetailPanel";

type BienFilter = 'all' | 'ineligible' | 'eligible'

const PAGE_SIZE = 20
const PAGE_SIZE_OPTIONS = [10, 20, 50]

/** Même vocabulaire visuel que l'écran d'éligibilité du parc (pill + point de couleur + teinte de ligne). */
type BienStatusKey = 'eligible' | 'eligibleAvecAlerte' | 'sansLocataire' | 'aValider' | 'exclu'

const STATUS_META: Record<BienStatusKey, { label: string; pill: string; dot: string; row: string }> = {
  eligible: {
    label: "Éligible",
    pill: "bg-green-50 text-green-700 border-green-200",
    dot: "bg-green-500",
    row: ""
  },
  // Bien éligible mais au moins un locataire non valide : même libellé, pastille en jaune.
  eligibleAvecAlerte: {
    label: "Éligible",
    pill: "bg-amber-50 text-amber-700 border-amber-200",
    dot: "bg-amber-500",
    row: ""
  },
  aValider: {
    label: "Locataires non valides",
    pill: "bg-red-50 text-red-700 border-red-200",
    dot: "bg-red-500",
    row: "bg-red-50/40"
  },
  sansLocataire: {
    label: "Sans locataire",
    pill: "bg-red-50 text-red-700 border-red-200",
    dot: "bg-red-500",
    row: "bg-red-50/50"
  },
  exclu: {
    label: "Exclu de la couverture",
    pill: "bg-gray-100 text-gray-500 border-gray-200",
    dot: "bg-gray-400",
    row: "opacity-60"
  }
}

const bienStatus = (bien: ParcTenancyBienDto): BienStatusKey => {
  if (bien.isIncluded === false) return 'exclu'
  if (bien.isValid) return bien.tenants.some(tenant => !tenant.isValid) ? 'eligibleAvecAlerte' : 'eligible'
  if (bien.tenants.length === 0) return 'sansLocataire'
  return 'aValider'
}

interface ParcTenancyDashboardProps {
  riskId: string
  file: ParcTenancyFileResponse
  refetch: () => void
}

export const ParcTenancyDashboard: FC<ParcTenancyDashboardProps> = ({ riskId, file, refetch }) => {
  const { openPanel } = usePanel()
  const contextValue = useSubscriptionProcess()

  const [filter, setFilter] = useState<BienFilter>('all')
  const [search, setSearch] = useState("")
  const [togglingBienId, setTogglingBienId] = useState<string>()

  const filteredBiens = useMemo(() => {
    let items = file.items
    switch (filter) {
      // Éligible/inéligible ne concerne que les biens couverts ; les exclus restent visibles dans « Tous ».
      case 'ineligible': items = items.filter(b => b.isIncluded !== false && !b.isValid); break
      case 'eligible': items = items.filter(b => b.isIncluded !== false && b.isValid); break
    }
    const term = search.trim().toLowerCase()
    if (!term) return items
    return items.filter(
      (b) => b.name?.toLowerCase().includes(term) || formatAddress(b.address).toLowerCase().includes(term)
    )
  }, [file, filter, search])

  /** Détail du bien (propriétaires + locataires) — remplace l'ancien accordéon. */
  const openBienDetail = useCallback((bien: ParcTenancyBienDto) => {
    openPanel({
      title: `${bien.name} — ${formatAddress(bien.address)}`,
      size: Sizes["4xl"],
    },
      <SubscriptionProcessContext.Provider value={contextValue}>
        <ParcBienDetailPanel riskId={riskId} bienId={bien.id} onChanged={refetch} />
      </SubscriptionProcessContext.Provider>
    )
  }, [openPanel, contextValue, riskId, refetch])

  /** Coche = couvert, décoche = écarté (réversible) — même pattern que la sélection des biens du parc. */
  const toggleInclusion = useCallback(async (bien: ParcTenancyBienDto) => {
    setTogglingBienId(bien.id)
    try {
      if (bien.isIncluded === false) {
        await realEstateAPI.includeBienInParcCoverage({ riskId, bienId: bien.id })
        toast.success(`${bien.name} réintégré dans la couverture.`)
      } else {
        await realEstateAPI.excludeBienFromParcCoverage({ riskId, bienId: bien.id })
        toast.success(`${bien.name} exclu de la couverture.`)
      }
      refetch()
    } catch (error) {
      console.error(error)
      toast.error("Impossible de modifier la couverture de ce bien.")
    } finally {
      setTogglingBienId(undefined)
    }
  }, [riskId, refetch])

  const filterChip = (key: BienFilter, label: string, count: number) => (
    <button
      type="button"
      onClick={() => setFilter(key)}
      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${filter === key
        ? 'bg-primary text-white border-primary'
        : 'bg-white text-gray-700 border-gray-200 hover:border-primary'}`}
    >
      {label} ({count})
    </button>
  )

  const columns = useMemo<Column<ParcTenancyBienDto>[]>(() => [
    {
      key: "isIncluded",
      label: "Inclure",
      align: "center",
      width: "72px",
      className: "!px-3",
      render: (_value, row) => (
        <div className="flex justify-center">
          <Checkbox
            checked={row.isIncluded !== false}
            disabled={togglingBienId === row.id}
            onChange={() => toggleInclusion(row)}
          />
        </div>
      )
    },
    {
      key: "name",
      label: "Bien",
      sortable: true,
      className: "!whitespace-normal align-top !px-3",
      render: (_value, row) => (
        <div className="flex items-start gap-3">
          <img
            src="/assets/images/real_estate_default.png"
            alt={row.name}
            className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
            loading="lazy"
          />
          <div className="flex flex-col min-w-0">
            <span className="font-medium text-gray-900 truncate">{row.name}</span>
            <span className="text-xs text-gray-500 truncate">{formatAddress(row.address)}</span>
          </div>
        </div>
      )
    },
    {
      key: "isValid",
      label: "Statut",
      width: "220px",
      className: "!whitespace-normal align-top !px-3",
      render: (_value, row) => {
        const meta = STATUS_META[bienStatus(row)]
        return (
          <span className={`inline-flex w-fit items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border ${meta.pill}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
            {meta.label}
          </span>
        )
      }
    },
    {
      key: "tenants",
      label: "Locataires",
      width: "160px",
      className: "!px-3",
      render: (_value, row) => {
        const validCount = row.tenants.filter(tenant => tenant.isValid).length
        if (row.tenants.length === 0) {
          return <span className="text-xs text-red-600">Aucun locataire</span>
        }
        return (
          <span className="text-sm text-gray-700">
            {row.tenants.length} locataire{row.tenants.length > 1 ? 's' : ''}
            <span className="text-gray-400"> · {validCount} valide{validCount > 1 ? 's' : ''}</span>
          </span>
        )
      }
    },
    {
      key: "rentWithCharges",
      label: "Loyer CC",
      align: "right",
      width: "110px",
      className: "!px-3",
      render: (_value, row) => (
        <span className="font-medium text-gray-900">{formatEuroPrice(row.rentWithCharges)}</span>
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [togglingBienId, toggleInclusion])

  const excludedCount = useMemo(() => file.items.filter(b => b.isIncluded === false).length, [file])

  return <div className="space-y-4">
    {
      file.blockingCount > 0
        ? <GenericMessage
          message={`${file.blockingCount} bien(s) inéligible(s) : saisissez leurs locataires ou décochez-les pour les écarter de la couverture.`}
          type="warning"
          dismissible={false} />
        : <GenericMessage
          message="Tous les biens couverts sont éligibles : chaque bien a au moins un locataire valide."
          type="info"
          dismissible={false} />
    }

    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-2">
        {filterChip('all', 'Tous', file.items.length)}
        {filterChip('eligible', 'Éligibles', file.validCount)}
        {filterChip('ineligible', 'Inéligibles', file.blockingCount)}
        {excludedCount > 0 &&
          <span className="text-sm text-gray-500">dont {excludedCount} exclu{excludedCount > 1 ? 's' : ''} de la couverture</span>}
      </div>
      <div className="relative w-full sm:max-w-md sm:ml-auto">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un bien, une adresse…"
          className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
        />
      </div>
    </div>

    <GenericTable<ParcTenancyBienDto>
      config={{
        variant: "soft",
        data: filteredBiens,
        columns,
        getRowClassName: (row) => STATUS_META[bienStatus(row)].row,
        actions: {
          row: [
            {
              label: "bien-detail",
              render: (row: ParcTenancyBienDto, index: number) => (
                <Button key={`detail-${index}`} round color="slate" title="Voir le détail du bien" onClick={() => openBienDetail(row)}>
                  <EyeIcon className="w-5 h-5" />
                </Button>
              )
            }
          ]
        },
        pagination: {
          enabled: true,
          pageSize: PAGE_SIZE,
          totalItems: filteredBiens.length,
          totalPages: Math.max(1, Math.ceil(filteredBiens.length / PAGE_SIZE)),
          showInfo: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS
        }
      }}
    />
  </div>
}
